import type { OnInit } from '@angular/core';
import { ChangeDetectorRef, Component, inject } from '@angular/core';
import type { UntypedFormGroup } from '@angular/forms';
import { UntypedFormBuilder, UntypedFormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { RecombinationsService } from '../../rcr-exchange/services/recombinations.service';
import { FmLoadingPageService } from '../../shared/services/fm-loading-page.service';
import { UserService } from '../../shared/services/user.service';
import type {
  Dealer,
  ListOfDealerTraderResponse,
  Trader,
} from '../services/interfaces/list-dealer-trader-response';
import { PassdataService } from '../services/passdata.service';
import { RcrExchangeNavigationServiceService } from '../services/rcr-exchange-navigation-service.service';

@Component({
  standalone: false,
  selector: 'invd-request-an-exchange-lookup',
  templateUrl: './request-an-exchange-lookup.component.html',
  styleUrls: ['./request-an-exchange-lookup.component.scss'],
})
export class RequestAnExchangeLookupComponent implements OnInit {
  public dropDownTitle = 'Select Exchange Type';
  public exchangeHasError = false;
  public selectedExchangeType: string;
  public selectedExchangeTypeIndex: number;
  public disableMenu = true;
  public isExTypeSelected = false;
  public isErrorMessage = true;
  public exchangeErrorMessage: any;
  public exchangeTypeArray: string[];
  public isInternalUsr = true;
  public dealers: Dealer[];
  public requestExchangeForm: UntypedFormGroup;
  public selectedDealer: Dealer = null;
  public selectedTrader: Trader = null;
  public isTraderSelected = false;
  public isApplyButtonClicked = true;

  private router = inject(Router);
  private recombinationService = inject(RecombinationsService);
  private passData = inject(PassdataService);
  private rcrNavigationService = inject(RcrExchangeNavigationServiceService);
  private fb = inject(UntypedFormBuilder);
  private userService = inject(UserService);
  private fmLoadingPageService = inject(FmLoadingPageService);
  private cdr = inject(ChangeDetectorRef);

  ngOnInit() {
    this.fmLoadingPageService.isLoaderRequesting.set(true);
    this.isApplyButtonClicked = true;
    this.isInternalUsr = this.userService.isInternalUser;
    this.passData.setExchangeConfirmationNumber(null);
    this.requestExchangeForm = this.fb.group({
      dealerControl: new UntypedFormControl(),
      traderControl: new UntypedFormControl(),
    });
    this.getListDealerTraders();

    this.exchangeTypeArray = [
      'Single Family REMIC',
      'SMBS',
      'Multi Family REMIC',
      'Benchmark REMIC',
    ];

    if (this.exchangeTypeArray.length > 0) {
      this.disableMenu = false;
    } else {
      this.disableMenu = true;
    }

    this.showExchangeErrorMessage(false, null);
  }

  private showExchangeErrorMessage(error, errorObject): void {
    if (error) {
      this.exchangeErrorMessage = new Object();
      this.exchangeHasError = error;
      this.exchangeErrorMessage.title = 'Status';
      this.exchangeErrorMessage.description = errorObject.error.messages[0].errorMessage;
    } else {
      this.exchangeErrorMessage = null;
    }
  }

  public setSelectedDealer(mDealer: Dealer) {
    this.selectedDealer = mDealer;
    this.selectedTrader = null;

    if (this.selectedDealer != null) {
      this.requestExchangeForm.controls.traderControl.reset();
      this.userService.setDealerName(mDealer.dealerName);
    }
  }

  public setSelectedTrader(mTrader: Trader) {
    this.selectedTrader = mTrader;
    if (mTrader != null) {
      this.userService.setTrader(
        mTrader.traderFirstName + ' ' + mTrader.traderLastName,
        mTrader.traderId,
      );
      this.isTraderSelected = true;
    } else {
      this.isTraderSelected = false;
    }
  }

  public selectDealType(idx: number, mType: string) {
    if (mType != null) {
      this.selectedExchangeType = mType;
      this.dropDownTitle = mType;
      this.isExTypeSelected = true;
      this.selectedExchangeTypeIndex = idx;
    }
  }

  public dealSearch(dealSearchObj: any): void {
    if (
      this.selectedTrader !== null &&
      this.selectedDealer !== null &&
      this.selectedDealer !== undefined &&
      this.selectedTrader !== undefined &&
      this.isApplyButtonClicked
    ) {
      const recombinationRequestData: any = new Object();
      recombinationRequestData.dealSubType = dealSearchObj.dealSubType;
      recombinationRequestData.userSearchInput = dealSearchObj.userSearchInput
        .toString()
        .toUpperCase();
      recombinationRequestData.takedownFlow = false;
      recombinationRequestData.sttsTraderId = this.userService.getTraderId();
      this.fmLoadingPageService.isLoaderRequesting.set(true);
      this.recombinationService.setRecombinationRequestData(recombinationRequestData);
      this.passData.setRecombinationDataCache(null);

      //#TODO should handle input validation at the input-validation component
      if (dealSearchObj.userSearchInput === undefined) {
        dealSearchObj.userSearchInput = '';
      }

      this.rcrNavigationService.canNavigate = true;
      this.router
        .navigate(['rcr-exchange/', dealSearchObj.userSearchInput, 'enter-balances'])
        .then((navigated) => {
          this.isApplyButtonClicked = true;
          this.fmLoadingPageService.isLoaderRequesting.set(false);
          if (!navigated) {
            const recombinationResponse: any =
              this.recombinationService.getRecombinationResponseData();
            this.showExchangeErrorMessage(true, recombinationResponse);
            this.isApplyButtonClicked = true;
            window.scrollTo(0, 0);
          }
        });
    }

    this.isApplyButtonClicked = false;
  }

  private getListDealerTraders(): void {
    if (this.passData.getCustomers() != null) {
      this.dealers = this.passData.getCustomers();
      this.fmLoadingPageService.isLoaderRequesting.set(false);
      if (!this.isInternalUsr) {
        this.selectedDealer = this.dealers[0];
        this.selectedTrader = this.dealers[0].traders[0];
        this.userService.setDealerName(this.selectedDealer.dealerName);
        this.userService.setTrader(
          this.selectedTrader.traderFirstName + ' ' + this.selectedTrader.traderLastName,
          this.selectedTrader.traderId,
        );
        this.isTraderSelected = true;
      }
      return;
    }
    this.recombinationService.getListDealersTradersService().subscribe({
      next: (data) => this.handelListDealerTraderServiceSuccess(data as ListOfDealerTraderResponse),
      error: (error) => this.handleListDealerTraderServiceFail(error),
    });
  }

  private handelListDealerTraderServiceSuccess(listDealerTrader: ListOfDealerTraderResponse) {
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.dealers = listDealerTrader.dealer;
    this.passData.setCustomers(this.dealers);
    if (!this.isInternalUsr) {
      this.selectedTrader = this.dealers[0].traders[0];
      this.selectedDealer = this.dealers[0];
      this.userService.setDealerName(this.selectedDealer.dealerName);
      this.userService.setTrader(
        this.selectedTrader.traderFirstName + ' ' + this.selectedTrader.traderLastName,
        this.selectedTrader.traderId,
      );
      this.isTraderSelected = true;
    }
    this.cdr.markForCheck();
  }

  private handleListDealerTraderServiceFail(_error: any) {
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.cdr.markForCheck();
  }

  protected trackByIdentity(_index: number, obj: any) {
    return obj;
  }
}
